// Durable outbox for frontend logs (BEAST-14120), shared by every tab and the SharedWorker of this browser
// profile. Logs are written here every few seconds and sent later by logOutboxSender, so they survive a
// network outage, a tab reload or close, and the death of the SharedWorker. Works in a tab and in a worker
// (no DOM). Records: { key, source, username, ts, size, event, authorPending? }. authorPending marks the logs a
// tab wrote before it knew its user: they are written right away (a crash must not lose them) and get their
// author later (settlePendingAuthor).
import { openDB } from 'idb';
import { createLogsDroppedEvent } from 'utils/logging/boundedLogBuffer';

export const OUTBOX_DB_NAME = 'aiq-logs';
export const OUTBOX_STORE = 'outbox';
export const META_STORE = 'meta';
const META_KEY = 'state';

export const OUTBOX_MAX_BYTES = 100 * 1024 * 1024;
export const OUTBOX_MAX_RECORDS = 50_000;
export const OUTBOX_MAX_AGE_MS = 24 * 60 * 60 * 1000;
export const PERSIST_INTERVAL_MS = 2000;
// One write transaction stays small so no single task on the main thread takes long.
export const MAX_PERSIST_CHUNK_BYTES = 200_000;
// Every settle transaction reads its records before rewriting them, so the key count also bounds what is read.
export const SETTLE_CHUNK_KEYS = 100;
// A normal write including its commit takes a few ms; only a real stall is worth a warning.
const SLOW_PERSIST_MS = 50;

const writeOptions = { durability: 'relaxed' };

// Write transaction whose tx.done never ends up as an unhandledrejection, even when an earlier await in the
// caller throws first (the caller still awaits tx.done and gets the error).
function writeTransaction(db, stores) {
    const tx = db.transaction(stores, 'readwrite', writeOptions);
    tx.done.catch(() => {});
    return tx;
}

let dbPromise = null;
let availability = null;
let unavailableSince = 0;
let diagnosticLog = () => {};
// An outbox that failed to open is checked again after this long (a worker lives for hours).
export const AVAILABILITY_RECHECK_MS = 60_000;

const defaultMeta = () => ({
    totalBytes: 0,
    lastSendAt: 0,
    nextAttemptAt: 0,
    failures: 0,
    workerSeenAt: 0,
    lastForcedAttemptAt: 0,
    firstFailureAt: 0,
});

// Lets the caller pass its original (not wired) console, so outbox diagnostics never feed back into the logs.
export function setLogOutboxDiagnostics(log) {
    diagnosticLog = log ?? (() => {});
}

function getDb() {
    if (!dbPromise) {
        // Only this connection is forgotten: an older one closing late must not drop a newer, healthy connection
        // (the next write would open a third one and leave the second open, blocking later schema upgrades).
        const forget = () => {
            if (dbPromise !== opening) return;
            dbPromise = null;
            availability = null;
        };
        // Inside then: indexedDB.open can also throw synchronously, which would escape the promise chain.
        const opening = Promise.resolve().then(() =>
            openDB(OUTBOX_DB_NAME, 1, {
                upgrade(db) {
                    const outbox = db.createObjectStore(OUTBOX_STORE, { keyPath: 'key', autoIncrement: true });
                    outbox.createIndex('by_ts', 'ts');
                    db.createObjectStore(META_STORE);
                },
                // A newer version (schema change) is being opened by a freshly loaded tab: release this connection,
                // otherwise its upgrade stays blocked for as long as this older tab or worker is open. The next
                // write reopens the database.
                blocking(currentVersion, blockedVersion, event) {
                    event.target.close();
                    forget();
                },
                // The browser closed the connection itself (site data cleared, storage pressure): open it again on the
                // next write, instead of failing every write into the in-memory fallback until a reload.
                terminated: forget,
            })
        );
        // A failed open is not kept either, so a later attempt can succeed.
        opening.catch(() => {
            if (dbPromise === opening) dbPromise = null;
        });
        dbPromise = opening;
    }
    return dbPromise;
}

export function isLogOutboxAvailable() {
    if (unavailableSince && Date.now() - unavailableSince >= AVAILABILITY_RECHECK_MS) {
        availability = null;
        unavailableSince = 0;
    }
    if (!availability) {
        availability =
            typeof indexedDB === 'undefined'
                ? Promise.resolve(false)
                : getDb().then(
                      () => true,
                      () => {
                          unavailableSince = Date.now();
                          return false;
                      }
                  );
    }
    return availability;
}

// Beaver puts the raw console arguments into the payload; IndexedDB stores them by structured clone, which
// throws on functions (e.g. an axios error's config) and would fail the whole write. A JSON round trip keeps
// exactly what used to be sent before (beaver serialized the payload to JSON), and gives the size for free.
export function toStorableEvent(event) {
    try {
        const json = JSON.stringify(event);
        return { event: JSON.parse(json), size: json.length };
    } catch {
        const fallback = {
            level: event?.level ?? 'warn',
            event: event?.event ?? 'log',
            payload: { 0: '[log event could not be serialized]', timestamp: String(event?.payload?.timestamp ?? Date.now()) },
        };
        return { event: fallback, size: JSON.stringify(fallback).length };
    }
}

function toRecord({ source, username, event, authorPending }) {
    const ts = Number(event?.payload?.timestamp) || Date.now();
    const storable = toStorableEvent(event);
    const record = { source: source ?? null, username: authorPending ? null : (username ?? null), ts, size: storable.size, event: storable.event };
    if (authorPending) record.authorPending = true;
    return record;
}

function chunkBySize(records, maxBytes) {
    const chunks = [];
    let chunk = [];
    let bytes = 0;
    for (const record of records) {
        if (chunk.length && bytes + record.size > maxBytes) {
            chunks.push(chunk);
            chunk = [];
            bytes = 0;
        }
        chunk.push(record);
        bytes += record.size;
    }
    if (chunk.length) chunks.push(chunk);
    return chunks;
}

async function addRecords(db, records) {
    const tx = writeTransaction(db, [OUTBOX_STORE, META_STORE]);
    const outbox = tx.objectStore(OUTBOX_STORE);
    const metaStore = tx.objectStore(META_STORE);
    const meta = (await metaStore.get(META_KEY)) ?? defaultMeta();
    const bytes = records.reduce((sum, record) => sum + record.size, 0);
    // Every request is awaited together with tx.done: when the transaction aborts (quota, full disk) each
    // pending request rejects too, and an unawaited one becomes an unhandledrejection - which the tab logs as an
    // error, which flushes, which fails again (a loop).
    const [keys] = await Promise.all([
        Promise.all(records.map((record) => outbox.add(record))),
        metaStore.put({ ...meta, totalBytes: meta.totalBytes + bytes }, META_KEY),
        tx.done,
    ]);
    return keys;
}

/**
 * Writes log events into the outbox.
 * @param {Object} options
 * @param {string|null} options.source - log_source (tab uid or 'SharedWorker')
 * @param {string|null} [options.username] - Author of the events
 * @param {Array} options.events - beaver events
 * @param {boolean} [options.authorPending] - The author is not known yet; stored without one until settlePendingAuthor
 * @returns {Promise<number[]>} Keys of the written records, in order
 */
export async function appendLogEvents({ source, username, events, authorPending = false }) {
    if (!events?.length) return [];
    const db = await getDb();
    const records = events.map((event) => toRecord({ source, username, event, authorPending }));
    const keys = [];
    for (const chunk of chunkBySize(records, MAX_PERSIST_CHUNK_BYTES)) {
        const startedAt = performance.now();
        keys.push(...(await addRecords(db, chunk)));
        const duration = performance.now() - startedAt;
        if (duration > SLOW_PERSIST_MS) {
            diagnosticLog(`[LogOutbox] Persisting ${chunk.length} log events took ${Math.round(duration)}ms`);
        }
    }
    return keys;
}

/**
 * Gives the waiting records with these keys their author (null = it stays unknown) and makes them sendable.
 * By key, not by source: before a tab knows its uid its records have no source, like those of every other
 * starting tab, so only the keys tell whose they are. Records already sent or dropped are skipped.
 * @param {Object} options
 * @param {number[]} options.keys - Keys of the records a tab wrote while waiting for its author
 * @param {string|null} options.username - Their author, or null when it will never be known
 */
export async function settlePendingAuthor({ keys, username }) {
    if (!keys?.length) return;
    const db = await getDb();
    // Several small transactions, like appendLogEvents: after a slow /auth there can be thousands of keys. Each one
    // stays below SETTLE_CHUNK_KEYS keys and MAX_PERSIST_CHUNK_BYTES of rewritten records.
    for (let start = 0; start < keys.length; ) {
        start += await settleChunk(db, keys.slice(start, start + SETTLE_CHUNK_KEYS), username);
    }
}

// Settles the records of these keys, in order, until the rewritten records reach MAX_PERSIST_CHUNK_BYTES (always at
// least one). Returns how many keys it handled; the rest go into the next transaction.
async function settleChunk(db, keys, username) {
    const tx = writeTransaction(db, OUTBOX_STORE);
    // An abort rejects every request before the Promise.all that awaits it is reached, see addRecords.
    const reads = keys.map((key) => tx.store.get(key));
    reads.forEach((read) => read.catch(() => {}));
    const records = await Promise.all(reads);
    const updates = [];
    let bytes = 0;
    let handled = 0;
    for (const record of records) {
        if (record?.authorPending) {
            const size = record.size ?? 0;
            if (updates.length && bytes + size > MAX_PERSIST_CHUNK_BYTES) break;
            bytes += size;
            const settled = { ...record, username: username ?? null };
            delete settled.authorPending;
            const update = tx.store.put(settled);
            update.catch(() => {});
            updates.push(update);
        }
        handled += 1;
    }
    await Promise.all([...updates, tx.done]);
    return handled;
}

export async function readOldest(limit) {
    const db = await getDb();
    return db.getAll(OUTBOX_STORE, undefined, limit);
}

// How many records readSendable looks at, per record it may return, before it stops (bounds one call when nearly
// everything is waiting for its author).
export const READ_SENDABLE_SCAN_FACTOR = 5;

/**
 * The oldest records that can be sent now, in key order. Skips the records still waiting for their author (younger
 * than pendingMaxAgeMs) and, to keep the order of each source, every later record of the same source. Records without
 * a source (tabs that did not know their uid yet) are not one source, so they never hold each other back.
 * A cursor that reads past the waiting records, so a large waiting backlog at the head (e.g. every tab reloaded at
 * once while /auth is slow) cannot hold back the logs of the other tabs and the worker.
 * @param {Object} options
 * @param {number} options.limit - Most records to return
 * @param {number} options.now - Current time, for the age of the waiting records
 * @param {number} options.pendingMaxAgeMs - A waiting record older than this is sent without its author
 * @returns {Promise<Array>} Sendable records, oldest first
 */
export async function readSendable({ limit, now, pendingMaxAgeMs }) {
    const db = await getDb();
    const isWaiting = (record) => record.authorPending && now - record.ts < pendingMaxAgeMs;
    // Fast path, the usual case: nothing at the head waits for its author, so one getAll request is enough. The
    // cursor below needs one request per record and is only worth it when there is something to read past.
    const oldest = await db.getAll(OUTBOX_STORE, undefined, limit);
    if (!oldest.some(isWaiting)) return oldest;

    const tx = db.transaction(OUTBOX_STORE, 'readonly');
    tx.done.catch(() => {});
    const waitingSources = new Set();
    const sendable = [];
    let scanned = 0;
    let cursor = await tx.store.openCursor();
    while (cursor && sendable.length < limit && scanned < limit * READ_SENDABLE_SCAN_FACTOR) {
        const record = cursor.value;
        scanned += 1;
        if (!waitingSources.has(record.source)) {
            if (isWaiting(record)) {
                if (record.source != null) waitingSources.add(record.source);
            } else {
                sendable.push(record);
            }
        }
        cursor = await cursor.continue();
    }
    return sendable;
}

export async function deleteRecords(records) {
    if (!records?.length) return;
    const db = await getDb();
    const tx = writeTransaction(db, [OUTBOX_STORE, META_STORE]);
    const outbox = tx.objectStore(OUTBOX_STORE);
    const metaStore = tx.objectStore(META_STORE);
    const meta = (await metaStore.get(META_KEY)) ?? defaultMeta();
    const bytes = records.reduce((sum, record) => sum + (record.size ?? 0), 0);
    // Awaited together with tx.done, see addRecords.
    await Promise.all([
        ...records.map((record) => outbox.delete(record.key)),
        metaStore.put({ ...meta, totalBytes: Math.max(0, meta.totalBytes - bytes) }, META_KEY),
        tx.done,
    ]);
}

export async function getOutboxMeta() {
    const db = await getDb();
    return (await db.get(META_STORE, META_KEY)) ?? defaultMeta();
}

export async function updateOutboxMeta(patch) {
    const db = await getDb();
    const tx = writeTransaction(db, META_STORE);
    const meta = (await tx.store.get(META_KEY)) ?? defaultMeta();
    await Promise.all([tx.store.put({ ...meta, ...patch }, META_KEY), tx.done]);
}

/**
 * Drops the oldest records while the outbox is over any limit (size, count, age) and leaves one
 * `logs.dropped` event per source, so the gap shows up in Grafana. Called by the sender, under its lock.
 */
export async function enforceLimits(now = Date.now(), limits = {}) {
    const { maxBytes = OUTBOX_MAX_BYTES, maxRecords = OUTBOX_MAX_RECORDS, maxAgeMs = OUTBOX_MAX_AGE_MS } = limits;
    const db = await getDb();
    const tx = writeTransaction(db, [OUTBOX_STORE, META_STORE]);
    const outbox = tx.objectStore(OUTBOX_STORE);
    const metaStore = tx.objectStore(META_STORE);
    const meta = (await metaStore.get(META_KEY)) ?? defaultMeta();

    let count = await outbox.count();
    let totalBytes = meta.totalBytes;
    const cutoff = now - maxAgeMs;
    const droppedBySource = new Map();

    // The cursor only reads, to find the last record to drop and build the reports; the records are then deleted
    // with one range request instead of one request per record.
    let lastDroppedKey = null;
    let cursor = await outbox.openCursor();
    while (cursor) {
        const record = cursor.value;
        const reasons = [];
        if (totalBytes > maxBytes) reasons.push('size');
        if (count > maxRecords) reasons.push('count');
        if (record.ts < cutoff) reasons.push('age');
        if (!reasons.length) break;

        const sourceKey = `${record.source}|${record.username}`;
        const dropped = droppedBySource.get(sourceKey) ?? {
            source: record.source,
            username: record.username,
            count: 0,
            from: record.ts,
            reasons: new Set(),
        };
        dropped.count += 1;
        dropped.to = record.ts;
        reasons.forEach((reason) => dropped.reasons.add(reason));
        droppedBySource.set(sourceKey, dropped);

        lastDroppedKey = cursor.key;
        count -= 1;
        totalBytes -= record.size ?? 0;
        cursor = await cursor.continue();
    }
    if (lastDroppedKey !== null) await outbox.delete(IDBKeyRange.upperBound(lastDroppedKey));

    const reports = [...droppedBySource.values()].map(({ source, username, count: droppedCount, from, to, reasons }) =>
        toRecord({ source, username, event: createLogsDroppedEvent({ count: droppedCount, from, to, reason: [...reasons].join(','), now }) })
    );
    totalBytes += reports.reduce((sum, report) => sum + report.size, 0);

    // Awaited together with tx.done, see addRecords.
    await Promise.all([
        ...reports.map((report) => outbox.add(report)),
        metaStore.put({ ...meta, totalBytes: Math.max(0, totalBytes) }, META_KEY),
        tx.done,
    ]);
}

// Test-only: the open connection is module state.
export async function resetLogOutboxForTests() {
    const db = await dbPromise?.catch(() => null);
    db?.close();
    dbPromise = null;
    availability = null;
    unavailableSince = 0;
    diagnosticLog = () => {};
}
